import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function normalizeDateParam(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : undefined;
}

const LinkedInCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { linkedinLogin } = useAuth();
  const hasStartedCompletionRef = useRef(false);
  const isMountedRef = useRef(true);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const completeSignIn = async () => {
      if (hasStartedCompletionRef.current) return;
      hasStartedCompletionRef.current = true;

      const status = params.get('status');
      const callbackError = params.get('error');
      const token = params.get('token');

      if (callbackError || status === 'error') {
        throw new Error(callbackError || 'LinkedIn sign-in failed');
      }
      if (status !== 'success' || !token) {
        throw new Error('LinkedIn did not return a session token.');
      }

      const result = await linkedinLogin({
        accessToken: token,
        refreshToken: params.get('refresh_token') || undefined,
        accessTokenExpiresAt: normalizeDateParam(params.get('access_token_expires_at')),
        refreshTokenExpiresAt: normalizeDateParam(params.get('refresh_token_expires_at')),
      });

      if (!isMountedRef.current) return;

      if (result?.success || result?.data?.accessToken) {
        window.location.href = '/';
      } else {
        navigate('/signin', { state: { error: 'LinkedIn sign-in failed. Please try again.' }, replace: true });
      }
    };

    completeSignIn().catch((err) => {
      if (!isMountedRef.current) return;
      navigate('/signin', {
        state: { error: err instanceof Error ? err.message : 'LinkedIn sign-in failed' },
        replace: true,
      });
    });
  }, [linkedinLogin, navigate, params]);

  return (
    <div className="linkedin-callback-container">
      <div className="linkedin-callback-card">
        <h2>LinkedIn</h2>
        <p>Completing your LinkedIn sign-in...</p>
        <div className="spinner" />
      </div>
    </div>
  );
};

export default LinkedInCallback;
